import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useSupplierProfileStewardshipUi } from '@/hooks/useSupplierProfileStewardshipUi';
import { useOrganization } from '@/contexts/OrganizationContext';
import {
  RECORD_SUPPLIER_CLAIM_OFFER_SHOWN_RPC,
  supplierClaimCompanyLabel,
  supplierClaimRoute,
  type SupplierClaimCandidate,
  type SupplierClaimOfferSurface,
} from '@/lib/supplierClaimCandidate';

export interface SupplierClaimOfferCardProps {
  candidate: SupplierClaimCandidate;
  surface: SupplierClaimOfferSurface;
  currentOrganizationName?: string | null;
  onContinueAsCustomer: () => void;
  secondaryLabel: string;
}

interface SupplierClaimOfferCopy {
  title: string;
  body: string;
  primaryLabel: string;
}

const GENERIC_PROFILE = "your company's supplier profile";

/**
 * Offers already recorded in this page session, keyed by surface and listing. A route guard or a
 * context refetch can unmount and remount the card while the user still looks at the same offer;
 * that is one offer shown, so it is recorded once. A full page load starts a new session.
 */
const recordedOffers = new Set<string>();

/** Test-only: forget the offers recorded in this module's page session. */
export function resetRecordedSupplierClaimOffersForTests() {
  recordedOffers.clear();
}

/** Copy matrix approved in ENT-6459 (verbatim; `<company>` falls back to `<domain>`, then the generic phrase). */
export function buildSupplierClaimOfferCopy(
  candidate: SupplierClaimCandidate,
  currentOrganizationName?: string | null,
): SupplierClaimOfferCopy {
  const company = supplierClaimCompanyLabel(candidate);
  const domain = candidate.domain;
  const profile = company ? `${company}'s supplier profile` : GENERIC_PROFILE;
  const companyWithDomain = company && domain && company !== domain ? `${company} (${domain})` : company ?? 'Your company';

  let copy: SupplierClaimOfferCopy;
  if (candidate.claim_state === 'claimed_by_other') {
    copy = {
      title: company ? `Join ${company}'s team` : "Join your company's team",
      body: `${company ? `${company}'s` : "Your company's"} supplier profile is already managed by a colleague. Ask to join their team.`,
      primaryLabel: 'Request to join',
    };
  } else if (candidate.claim_state === 'managed_requires_approval') {
    copy = {
      title: company ? `${company}'s profile is maintained by Supply Guard` : "Your company's profile is maintained by Supply Guard",
      body: "Request to take it over and we'll confirm by email.",
      primaryLabel: 'Request stewardship',
    };
  } else if (candidate.source === 'invitation' && candidate.inviting_organization_name) {
    copy = {
      title: `${candidate.inviting_organization_name} invited you to claim ${profile}`,
      body: `Claiming the profile for ${domain || company || 'your company'} is free and needs no plan.`,
      primaryLabel: 'Claim supplier profile',
    };
  } else {
    copy = {
      title: `Here to manage ${profile}?`,
      body: `${companyWithDomain} is listed on the Supply Guard marketplace. Claiming the profile is free and needs no plan.`,
      primaryLabel: 'Claim supplier profile',
    };
  }

  if (candidate.current_organization_convertible === true && currentOrganizationName) {
    copy.body = `${copy.body} Your empty workspace "${currentOrganizationName}" will become your supplier workspace.`;
  }
  return copy;
}

/**
 * ENT-6459 (D2): offers the free supplier claim on a customer-path screen. It only navigates to
 * the canonical claim route and records that the offer was shown; it never creates an
 * organisation, membership, profile or claim.
 */
export function SupplierClaimOfferCard({
  candidate,
  surface,
  currentOrganizationName,
  onContinueAsCustomer,
  secondaryLabel,
}: SupplierClaimOfferCardProps) {
  const navigate = useNavigate();
  const { user } = useOrganization();
  const copy = buildSupplierClaimOfferCopy(candidate, currentOrganizationName);
  // Codex r4451325827: offer "Request stewardship" only where the claim page can act on it,
  // i.e. the exact exposure that page uses (an organisation plus the stewardship flag). Without
  // it the managed-profile state renders nothing and records no offer; there is no
  // organisation-less stewardship flow, and the team-join form cannot transfer stewardship.
  const stewardshipUi = useSupplierProfileStewardshipUi();
  const hidden = candidate.claim_state === 'managed_requires_approval' && !stewardshipUi;

  useEffect(() => {
    if (hidden) return;
    const key = `${surface}:${candidate.marketplace_listing_id}`;
    if (recordedOffers.has(key)) return;
    recordedOffers.add(key);
    void (async () => {
      try {
        const { error } = await supabase.rpc(RECORD_SUPPLIER_CLAIM_OFFER_SHOWN_RPC, {
          p_surface: surface,
          p_marketplace_listing_id: candidate.marketplace_listing_id,
        });
        if (error) throw error;
      } catch (error) {
        // Fire-and-forget: a failed audit write is reported, never shown to the user.
        const errorCode = typeof error === 'object' && error !== null && 'code' in error
          && typeof (error as { code?: unknown }).code === 'string'
          ? (error as { code: string }).code
          : 'unknown';
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: 'supplier_claim_offer',
            functionName: 'SupplierClaimOfferCard',
            rpcName: RECORD_SUPPLIER_CLAIM_OFFER_SHOWN_RPC,
            errorCode,
            surface,
          },
          extra: { rpcName: RECORD_SUPPLIER_CLAIM_OFFER_SHOWN_RPC, errorCode, surface },
          fingerprint: ['supplier-claim-offer', RECORD_SUPPLIER_CLAIM_OFFER_SHOWN_RPC, errorCode],
        });
      }
    })();
  }, [candidate.marketplace_listing_id, surface, hidden]);

  if (hidden) return null;

  return (
    <Card
      data-testid="supplier-claim-offer-card"
      data-surface={surface}
      data-claim-state={candidate.claim_state}
      className="border-primary bg-primary/5 shadow-none"
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{copy.title}</CardTitle>
        <CardDescription className="text-sm text-foreground/80">{copy.body}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button
          type="button"
          data-testid="supplier-claim-offer-primary"
          onClick={() => navigate(supplierClaimRoute(candidate, user?.email))}
        >
          {copy.primaryLabel}
        </Button>
        <Button
          type="button"
          variant="outline"
          data-testid="supplier-claim-offer-secondary"
          onClick={onContinueAsCustomer}
        >
          {secondaryLabel}
        </Button>
      </CardContent>
    </Card>
  );
}
